/**
 * WEB-3739 юнит 1 — меню профиля оболочки кабинета нового вида, по `MeMenu`
 * эталона `design/cabinet-rescin/app.jsx` (стили `.me-row`, `.me-o`, `.me-orgs`,
 * `.me-find`, `.me-none`, `.me-ro`, `.me-skel`, `.av` — `styles/cabinet-shell.css`).
 *
 * Кнопка `me`: аватар (фото, если есть `avatarUrl`, иначе первая буква на
 * заливке) и имя аккаунта (`username || email`, WEB-832; пока организации
 * грузятся — заглушка, WEB-665). Окно `me-pop` сверху вниз: организации
 * (текущая первой, от шести — поиск, список своей прокруткой, WEB-3758) и
 * «Создать рабочее пространство» (`OrgSwitcher`), черта, «Баланс», «Личные
 * настройки», «Документация», черта, «Выйти».
 *
 * Баланс — свободный остаток одной суммой (`selectWalletAvailableCents`, без
 * резерва и без красного; резерв — на странице «Баланс/Тариф»), пока кошелёк не
 * пришёл — заглушка, а не «$0.00» (WEB-2826). К пополнению (`/org/balance`, со
 * сбросом проекта — симметрия WEB-731) идут владелец и администратор активной
 * организации; остальным пункт погашен (случай PM 10.08 — правило кода, эталон
 * гасит только гостя). Загрузку и обновление кошелька держит оболочка
 * (`useShellBalance` в `DashboardLayout`), а не это меню.
 *
 * Закрытие — щелчок мимо, Esc, прокрутка мимо, «одно меню за раз» —
 * `useOutside` кита; клавиатура — `useMenuKeys` кита. Положение окна —
 * `placement` и `mini` по правилу эталона: `top` — под кнопкой справа, `side` —
 * над кнопкой (в боковом меню), `mini` — сбоку от узкого меню.
 * Корень `.ds` ставит тот, кто кладёт меню (боковое меню — `Sidebar.tsx`).
 */
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMenuKeys, useOutside } from '@/components/kit/ds';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import { logoutThunk } from '@/store/authSlice';
import { clearActiveProjectThunk } from '@/store/projectsSlice';
import { selectWalletAvailableCents } from '@/utils/walletAvailable';
import { formatCents } from '@/utils/formatCents';
import { useStrings, useUiLocale } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';
import { docsHomeUrl } from '@/components/project/app-connection/devDocsUrl';
import { OrgSwitcher } from '@/components/organizations/OrgSwitcher';
import { CreateOrganizationModal } from '@/components/organizations/CreateOrganizationModal';
import { useShellToast } from './shellToast';
import '../../styles/cabinet-shell.css';

export interface MeMenuProps {
  placement?: 'top' | 'side';
  mini?: boolean;
}

function popPosition(
  placement: 'top' | 'side',
  mini: boolean,
): React.CSSProperties {
  const pos: React.CSSProperties =
    placement === 'top'
      ? { top: 'calc(100% + 6px)', right: 0 }
      : mini
        ? { bottom: 0, left: 'calc(100% + 8px)' }
        : { bottom: 'calc(100% + 6px)', left: 0 };
  return { width: 240, top: 'auto', right: 'auto', ...pos };
}

export function MeMenu({ placement = 'side', mini = false }: MeMenuProps) {
  const s = useStrings(commonStrings);
  const locale = useUiLocale();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const { orgs, activeOrgId, isLoading } = useAppSelector(
    (state) => state.organizations,
  );
  const activeOrg = orgs.find((o) => o.id === activeOrgId);
  const accountLabel = user?.username || user?.email || '';
  const showSkel = !activeOrg && isLoading;
  const freeCents = useAppSelector(selectWalletAvailableCents);
  const billingLoaded = useAppSelector(
    (state) =>
      (state as { billing?: { billingLoaded?: boolean } }).billing
        ?.billingLoaded ?? false,
  );
  const canTopUp = activeOrg?.role === 'owner' || activeOrg?.role === 'admin';

  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // WEB-3796: итог смены организации показывает оболочка (ShellToastHost в
  // DashboardLayout), а не меню: строки OrgSwitcher убираются вместе с окном
  // меню до ответа сервера, а само меню на телефоне лежит в шторке aside.qm,
  // которая закрывается с visibility: hidden и гасила всплывашку внутри себя.
  const showToast = useShellToast();
  const ref = useRef<HTMLDivElement>(null);
  useOutside(ref, () => setOpen(false), open);
  useMenuKeys(ref, open, setOpen);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };
  const openBalance = () => {
    setOpen(false);
    void dispatch(clearActiveProjectThunk());
    navigate('/org/balance');
  };
  const logout = async () => {
    await dispatch(logoutThunk());
    navigate('/login');
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="me-row"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        title={showSkel ? undefined : accountLabel}
        data-testid="me"
        style={placement === 'top' ? { width: 'auto' } : undefined}
      >
        <span className="av" aria-hidden="true" data-testid="me-av">
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" />
          ) : (
            accountLabel.charAt(0).toUpperCase()
          )}
        </span>
        <span className="nm" data-testid="header-account-label">
          {showSkel ? <span className="me-skel" /> : accountLabel}
        </span>
      </button>
      {open && (
        <ul
          className="pop menu me-pop"
          role="menu"
          style={popPosition(placement, mini)}
          data-testid="me-pop"
        >
          <OrgSwitcher
            onSelect={() => setOpen(false)}
            onToast={showToast}
            onCreateClick={() => {
              setOpen(false);
              setCreateOpen(true);
            }}
          />
          <hr />
          <li
            role="menuitem"
            tabIndex={canTopUp ? 0 : -1}
            aria-disabled={canTopUp ? undefined : true}
            className={canTopUp ? 'opt' : 'opt me-ro'}
            title={canTopUp ? s.orgBalanceTopUpTooltip : s.orgBalanceTooltip}
            onClick={canTopUp ? openBalance : undefined}
            data-testid="me-balance"
          >
            <span>{s.meBalance}</span>
            {billingLoaded && freeCents !== null ? (
              <span className="num me-sum">{formatCents(freeCents)}</span>
            ) : (
              <span
                className="me-skel"
                data-testid="me-balance-load"
                aria-label={s.loading}
              />
            )}
          </li>
          <li
            role="menuitem"
            tabIndex={0}
            className="opt"
            onClick={() => go('/settings')}
            data-testid="me-personal"
          >
            {s.personalSettings}
          </li>
          <li role="none">
            <a
              role="menuitem"
              tabIndex={0}
              className="opt"
              href={docsHomeUrl(locale)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="user-menu-docs"
            >
              {s.documentation}
            </a>
          </li>
          <hr />
          <li
            role="menuitem"
            tabIndex={0}
            className="opt"
            onClick={() => void logout()}
            data-testid="me-logout"
          >
            {s.signOut}
          </li>
        </ul>
      )}
      <CreateOrganizationModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}
